import React from 'react';
import { useProjectCoverage } from '../../hooks/useProjectPhotos';
import { formatDayLabel } from '../../photoUpload/dayBuckets';
import { summarizePlan } from '../../lib/capturePlan';
import './CapturePlan.css';

// Bucket a per-day photo count into one of four heat levels relative to the
// busiest day. A day with any photos is at least level 1.
function heatLevel(count, maxCount) {
  if (!count) return 0;
  const ratio = maxCount > 0 ? count / maxCount : 0;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  return 4;
}

// Compact "plan vs actual capture days" glance for the Overview tab. Reads
// the per-project coverage rollup (one small doc) — the detailed per-day
// thumbnail grid stays on the Footage tab. Renders nothing until photos
// exist, so it never shows for a video-only project.
const CaptureCoverageStrip = ({ projectId, capturePlan }) => {
  const { coverage } = useProjectCoverage(projectId);
  const days = coverage?.days || {};
  const activeKeys = Object.keys(days).filter((k) => (days[k]?.photoCount || 0) > 0);
  if (activeKeys.length === 0) return null;

  // Dated days drive the timeline (oldest → newest); 'unknown' is called out
  // separately since it has no position on the calendar.
  const datedKeys = activeKeys.filter((k) => k !== 'unknown').sort();
  const unknownCount = days.unknown?.photoCount || 0;
  const maxCount = datedKeys.reduce((m, k) => Math.max(m, days[k].photoCount || 0), 0);

  const totalPhotos = coverage.totalPhotos
    || activeKeys.reduce((n, k) => n + (days[k].photoCount || 0), 0);
  const actualDays = datedKeys.length;

  const plan = summarizePlan(capturePlan);
  const dayNoun = (n) => `${n} day${n === 1 ? '' : 's'}`;

  return (
    <section className="cc-strip" data-testid="capture-coverage-strip">
      <div className="cc-strip-head">
        <h4 className="cc-strip-title">Capture coverage</h4>
        <span className="cc-strip-sub">
          {dayNoun(actualDays)} · {totalPhotos.toLocaleString()} photo{totalPhotos === 1 ? '' : 's'}
          {plan ? ` of ~${dayNoun(plan.plannedDays)} planned` : ''}
        </span>
      </div>

      <div
        className="cc-strip-cells"
        role="img"
        aria-label={`Photos captured on ${dayNoun(actualDays)}${plan ? ` of about ${plan.plannedDays} planned capture days` : ''}`}
      >
        {datedKeys.map((k) => {
          const count = days[k].photoCount || 0;
          const level = heatLevel(count, maxCount);
          return (
            <span
              key={k}
              className={`cc-cell${level ? ` cc-cell--l${level}` : ''}`}
              title={`${formatDayLabel(k)} — ${count.toLocaleString()} photo${count === 1 ? '' : 's'}`}
              data-testid="cc-cell"
            />
          );
        })}
      </div>

      {plan && plan.plannedDays > actualDays && (
        <p className="cc-strip-plan" data-testid="cc-plan-gap">
          {dayNoun(plan.plannedDays - actualDays)} of your planned capture window not yet covered.
        </p>
      )}

      {unknownCount > 0 && (
        <p className="cc-strip-unknown" data-testid="cc-unknown">
          + {unknownCount.toLocaleString()} photo{unknownCount === 1 ? '' : 's'} with no capture date
        </p>
      )}

      <div className="cc-strip-legend" aria-hidden="true">
        <span>Fewer</span>
        <span className="cc-cell cc-cell--l1" />
        <span className="cc-cell cc-cell--l2" />
        <span className="cc-cell cc-cell--l3" />
        <span className="cc-cell cc-cell--l4" />
        <span>More</span>
      </div>
    </section>
  );
};

export default CaptureCoverageStrip;
